/**
 * useClientBudgets Hooks (Story 18.4 read-list + 18.2 configure/pacing)
 *
 * Reads a client's configured budgets, reads its computed pacing, and saves
 * (replace-set) the budget configuration via the per-client budget endpoints.
 * Mirrors the useClientMarkups / useClientGoals shape: TanStack Query v5,
 * `enabled: !!clientId`, `{ data }` unwrap, typed rows, error → empty list.
 *
 * - `useClientBudgets(clientId)`         → GET /api/clients/:id/budgets
 *                                          key ["client-budgets", clientId]
 * - `useClientBudgetPacing(clientId)`    → GET /api/clients/:id/budget-pacing
 *                                          key ["client-budget-pacing", clientId]
 *                                          (the progress source — server-side
 *                                          demo/live spend resolution, the
 *                                          dashboard card consumes this)
 * - `useSaveClientBudgets(clientId)`     → PUT /api/clients/:id/budgets
 *                                          replace-set; invalidates BOTH keys on
 *                                          success so the card updates without a
 *                                          page reload (the "goal immediately
 *                                          appears" pattern, AC #7).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPut } from "../lib/apiClient";

// ============================================================================
// Types
// ============================================================================

export interface ClientBudgetRow {
  id: string;
  /** Platform slug (e.g. "google-ads") OR the `"all"` cross-platform sentinel. */
  integrationType: string;
  amount: number;
  currencyCode: string;
  periodType: "monthly" | "quarterly";
  periodStart: number | null;
  periodEnd: number | null;
}

/** One per-budget pacing entry from GET /:id/budget-pacing (Story 18.5). */
export interface ClientBudgetPacingEntry {
  budgetId: string;
  /** "all" = the cross-platform total; otherwise a platform slug. */
  integrationType: string;
  amount: number;
  currencyCode: string;
  periodType: string;
  /** Resolved window bounds (unix ms). */
  periodStart: number;
  periodEnd: number;
  spend: number;
  spentPct: number;
  elapsedPct: number;
  projectedSpend: number;
  projectedPct: number;
  paceStatus: "on_pace" | "at_risk" | "over";
  /** True when ≥1 platform's spend fetch failed (degraded, not fatal). */
  incomplete: boolean;
}

/** The replace-set PUT payload (AC #2). */
export interface SaveClientBudgetsPayload {
  periodType: "monthly" | "quarterly";
  /** null/omitted → no cross-platform total budget. */
  totalAmount?: number | null;
  platforms: Array<{ integrationType: string; amount: number }>;
}

// ============================================================================
// Stable query-key factories
// ============================================================================

export function clientBudgetsKey(clientId: string | undefined) {
  return ["client-budgets", clientId] as const;
}

export function clientBudgetPacingKey(clientId: string | undefined) {
  return ["client-budget-pacing", clientId] as const;
}

// ============================================================================
// Read: budgets list
// ============================================================================

export function useClientBudgets(clientId: string | undefined) {
  const { data: budgets = [], isLoading } = useQuery<ClientBudgetRow[]>({
    queryKey: clientBudgetsKey(clientId),
    queryFn: async () => {
      const res = await apiGet(`/api/clients/${clientId}/budgets`);
      if (!res.ok) return [];
      const json = await res.json();
      // Route shape: { data: { budgets: [...] } }.
      return (json?.data?.budgets ?? []) as ClientBudgetRow[];
    },
    enabled: !!clientId,
  });

  return { budgets, isLoading };
}

// ============================================================================
// Read: pacing (the progress source — the card consumes this)
// ============================================================================

export function useClientBudgetPacing(clientId: string | undefined) {
  const { data: pacing = [], isLoading } = useQuery<ClientBudgetPacingEntry[]>({
    queryKey: clientBudgetPacingKey(clientId),
    queryFn: async () => {
      const res = await apiGet(`/api/clients/${clientId}/budget-pacing`);
      if (!res.ok) return [];
      const json = await res.json();
      // Route shape: { data: { pacing: [...] } }.
      return (json?.data?.pacing ?? []) as ClientBudgetPacingEntry[];
    },
    enabled: !!clientId,
  });

  return { pacing, isLoading };
}

// ============================================================================
// Save: replace-set PUT (invalidates BOTH keys so the card refreshes)
// ============================================================================

export function useSaveClientBudgets(clientId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      payload: SaveClientBudgetsPayload,
    ): Promise<ClientBudgetRow[]> => {
      const res = await apiPut(`/api/clients/${clientId}/budgets`, payload);
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { detail?: string; title?: string }
          | null;
        throw new Error(
          body?.detail ??
            body?.title ??
            `Failed to save budgets (${res.status})`,
        );
      }
      const json = (await res.json()) as { data: { budgets: ClientBudgetRow[] } };
      return json.data.budgets;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: clientBudgetsKey(clientId),
      });
      void queryClient.invalidateQueries({
        queryKey: clientBudgetPacingKey(clientId),
      });
    },
  });
}
